import { useQuery } from '@tanstack/react-query';
import type { OperationEvent, OperationEventSummary, OperationEventLevel } from '@loader/shared-types';

import { useRpc } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';

interface PaginatedEvents {
  events: OperationEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Context needed to fetch operation events
 * Events are stored under operations, so we need tableId and operationId
 */
interface OperationContext {
  tableId: string;
  operationId: string;
}

/**
 * Hook to fetch event summary for an operation
 * Summary only counts validation_error level events
 *
 * @param context - The operation context (tableId and operationId)
 */
export function useOperationEventSummary(context?: OperationContext) {
  const rpc = useRpc();

  return useQuery<OperationEventSummary>({
    queryKey: context
      ? queryKeys.tables.operations.eventsSummary(context.tableId, context.operationId)
      : queryKeys.tables.operations.eventsSummaryDisabled,
    queryFn: async () => {
      if (!context) {
        throw new Error('Operation context is required');
      }

      // @ts-expect-error - RPC types may not be fully generated yet
      const res = await rpc.api.private.tables[':id'].operations[':operationId'].events.summary.$get({
        param: { id: context.tableId, operationId: context.operationId },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch event summary');
      }

      const data = await res.json();
      return data;
    },
    enabled: !!context?.tableId && !!context?.operationId,
    refetchOnWindowFocus: false,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch paginated events for an operation
 *
 * @param context - The operation context (tableId and operationId)
 * @param params - Pagination and filter parameters
 */
export function useOperationEvents(
  context?: OperationContext,
  params?: {
    page?: number;
    limit?: number;
    level?: OperationEventLevel;
    errorCode?: string;
    fieldId?: string;
  }
) {
  const rpc = useRpc();

  return useQuery<PaginatedEvents>({
    queryKey: context
      ? queryKeys.tables.operations.events(context.tableId, context.operationId, params)
      : queryKeys.tables.operations.eventsDisabled(params),
    queryFn: async () => {
      if (!context) {
        throw new Error('Operation context is required');
      }

      const queryParams: Record<string, string> = {};
      if (params?.page !== undefined) queryParams.page = String(params.page);
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      if (params?.level) queryParams.level = params.level;
      if (params?.errorCode) queryParams.error_code = params.errorCode;
      if (params?.fieldId) queryParams.field_id = params.fieldId;

      // @ts-expect-error - RPC types may not be fully generated yet
      const res = await rpc.api.private.tables[':id'].operations[':operationId'].events.$get({
        param: { id: context.tableId, operationId: context.operationId },
        query: queryParams,
      });

      if (!res.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await res.json();
      return data;
    },
    enabled: !!context?.tableId && !!context?.operationId,
    refetchOnWindowFocus: false,
    staleTime: 30000, // 30 seconds
  });
}

// Re-export for backwards compatibility during transition
export { useOperationEventSummary as useOperationErrorSummary };
export { useOperationEvents as useOperationErrors };

/**
 * Export types for use in components
 */
export type { OperationEventSummary, OperationEvent, PaginatedEvents, OperationContext };
